import { Link } from "react-router-dom";
import HeroCarousel from "./components/HeroCarousel";
import SectionHeading from "@/components/base/SectionHeading";
import EpisodeCard from "@/components/base/EpisodeCard";
import NewsCard from "@/components/base/NewsCard";
import { episodes } from "@/mocks/episodes";
import { characters } from "@/mocks/characters";
import { newsList } from "@/mocks/news";
import { siteInfo } from "@/mocks/siteInfo";
import { useSeo } from "@/hooks/useSeo";

const stats = [
  { value: "16", label: "全季剧集" },
  { value: "10", label: "角色深度解析" },
  { value: "6", label: "最新影视资讯" },
];

export default function Home() {
  useSeo({
    title: "年轻的母亲2电视剧剧情介绍与最新资讯",
    description:
      "年轻的母亲2官网提供该剧的剧情介绍、角色分析及相关资讯，涵盖全季剧集信息，方便用户了解剧情进展和人物关系。",
    keywords: "年轻的母亲2,电视剧,剧情介绍,剧集信息,角色分析,影视资讯,家庭剧",
  });

  const featuredEpisodes = episodes.slice(0, 6);
  const featuredCharacters = characters.slice(0, 5);
  const latestNews = newsList.slice(0, 3);

  return (
    <div className="w-full">
      <HeroCarousel />

      {/* 站点数据概览 */}
      <section className="w-full border-b border-background-200 bg-background-50">
        <div className="mx-auto grid w-full grid-cols-1 divide-y divide-background-200 px-4 sm:grid-cols-3 sm:divide-x sm:divide-y-0 md:px-6">
          {stats.map((stat) => (
            <div key={stat.label} className="flex items-center justify-center gap-3 py-5">
              <span className="font-heading text-2xl font-extrabold text-primary-600">
                {stat.value}
              </span>
              <span className="font-label text-sm text-foreground-600">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 剧集快捷入口 */}
      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="剧集速览"
          title="本季剧集快捷入口"
          desc="从第一集的搬家，到第十六集的阳光正好。点击任意卡片，进入该集的完整剧情介绍与人物动态。"
          action={
            <Link
              to="/episodes"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 font-label text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              查看全部剧集
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line text-base"></i>
              </span>
            </Link>
          }
        />
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {featuredEpisodes.map((ep) => (
            <EpisodeCard key={ep.id} {...ep} />
          ))}
        </div>
      </section>

      {/* 角色精选 */}
      <section className="w-full border-y border-background-200 bg-background-50">
        <div className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
          <SectionHeading
            eyebrow="人物关系"
            title="主要角色精选"
            desc="一个家庭的悲喜，由一群各有来处的人共同书写。点击头像，走进每个角色的内心世界。"
            action={
              <Link
                to="/characters"
                className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 font-label text-sm font-semibold text-foreground-800 transition-colors hover:border-accent-300 hover:text-accent-600"
              >
                全部角色
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-base"></i>
                </span>
              </Link>
            }
          />
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
            {featuredCharacters.map((ch) => (
              <Link
                key={ch.id}
                to={`/character/${ch.id}`}
                className="group flex flex-col items-center rounded-lg border border-background-200 bg-background-100 p-4 text-center transition-colors hover:border-accent-300 hover:bg-background-50"
              >
                <div className="h-24 w-24 overflow-hidden rounded-full border-2 border-background-200 bg-secondary-200 group-hover:border-accent-400">
                  <img
                    src={ch.avatar}
                    alt={`《年轻的母亲2》角色 ${ch.name}`}
                    title={`${ch.name} - 角色介绍`}
                    className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <h3 className="mt-3 font-heading text-base font-bold text-foreground-950">{ch.name}</h3>
                <p className="mt-1 font-label text-xs text-foreground-500">饰 {ch.actor}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 最新资讯 */}
      <section className="mx-auto w-full px-4 py-12 md:px-6 md:py-16">
        <SectionHeading
          eyebrow="资讯动态"
          title="最新影视资讯"
          desc="剧集口碑、幕后花絮与角色研究，第一时间掌握《年轻的母亲2》相关内容更新。"
          action={
            <Link
              to="/news"
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 font-label text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              前往资讯中心
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-arrow-right-line text-base"></i>
              </span>
            </Link>
          }
        />
        <div className="grid grid-cols-1 gap-4">
          {latestNews.map((item) => (
            <NewsCard key={item.id} {...item} />
          ))}
        </div>
      </section>

      {/* 站点简介 */}
      <section className="w-full border-t border-background-200 bg-primary-950">
        <div className="mx-auto w-full px-4 py-14 md:px-6">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-accent-500 px-3 py-1 font-label text-xs font-semibold text-primary-950">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-information-line text-sm"></i>
                </span>
                关于本站
              </span>
              <h2 className="mt-4 font-heading text-2xl font-extrabold tracking-tight text-background-50 md:text-3xl">
                {siteInfo.tagline}
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-background-300">
                {siteInfo.intro}
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-background-50 px-5 py-3 font-label text-sm font-semibold text-primary-950 transition-colors hover:bg-background-100"
              >
                了解更多
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-base"></i>
                </span>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { icon: "ri-file-list-3-line", title: "逐集解析", desc: "16 集剧情完整梳理" },
                { icon: "ri-user-heart-line", title: "角色分析", desc: "人物关系与动机解读" },
                { icon: "ri-newspaper-line", title: "影视资讯", desc: "口碑、花絮与动态" },
                { icon: "ri-search-eye-line", title: "全站搜索", desc: "剧集角色资讯一键查" },
              ].map((f) => (
                <div key={f.title} className="rounded-lg border border-primary-800 bg-primary-900/60 p-4">
                  <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-primary-950">
                    <i className={`${f.icon} text-xl`}></i>
                  </span>
                  <h3 className="mt-3 font-heading text-sm font-bold text-background-50">{f.title}</h3>
                  <p className="mt-1 text-xs text-background-400">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}